import { numberWithCommas } from '~/js/helpers'

export default class Product {
  constructor(product = {}) {
    this.available =
      product.available === undefined ? undefined : product.available
    this.categoryHandles = product.category_handles
    this.categoryTitles = product.category_titles
    this.comparePrice = product.compare_at_price
      ? product.compare_at_price / 100
      : undefined
    this.comparePriceMin =
      product.type && product.type.includes('オーダーカーテン') // 要確認
        ? null
        : product.compare_at_price_min
        ? product.compare_at_price_min / 100
        : undefined
    this.features = []
    this.handle = product.handle
    this.id = product.id
    this.price = product.price ? product.price / 100 : undefined
    this.priceMin = product.price_min ? product.price_min / 100 : undefined
    this.productImage = product.featured_image || ''
    this.productType = product.type || ''
    this.sku = product.sku
    this.stock = product.inventory_quantity
    this.tags = product.tags || []
    this.text1 = this.getFieldByTag('商品名追加テキスト1')
    this.text2 = this.getFieldByTag('商品名追加テキスト2')
    this.title = product.title
    this.url = product.url || `/products/${this.handle}`
    this.variants = product.variants || []
    this.variantId = this.variants.length && this.variants[0].id
    this.vendor = product.vendor

    this.color = this.getFieldByTag('カラー')
    this.itemGroupId = this.getFieldByTag('item_group_id')
  }

  getFieldByTag(tagPrefix) {
    const targetTag = this.tags.find((tag) => tag.includes(`${tagPrefix}:`))
    return targetTag ? targetTag.replace(`${tagPrefix}:`, '') : ''
  }

  get discountPercentage() {
    return Math.floor(
      ((this.comparePrice - this.price) * 100) / this.comparePrice
    )
  }

  get imageUrl() {
    return (
      this.productImage || "{{ 'no_image_b.jpg' | asset_img_url:'medium' }}"
    )
  }

  get isActionAvailable() {
    return (
      (this.productStatus === 'available' ||
        this.productStatus === 'presale') &&
      !this.productType.includes('オーダーカーテン') &&
      !this.isExhibit
    )
  }

  get isHandleValid() {
    return /^\d{13}$/.test(this.handle)
  }

  get isSale() {
    return (
      this.comparePriceMin !== null &&
      this.comparePriceMin > this.priceMin &&
      (this.productType.includes('廃番') || this.productType.includes('セール'))
    )
  }

  get isSoldout() {
    return this.productStatus === 'soldout'
  }

  get isBackstock() {
    return this.productStatus === 'backstock'
  }

  get productStatus() {
    if (
      this.productType.includes('予約品') &&
      !this.tags.includes('out_of_stock')
    )
      return 'presale'

    if (
      this.available === false ||
      this.tags.includes('out_of_stock') ||
      this.stock <= 0
    )
      if (this.tags.includes('not-bis')) return 'soldout'
      else if (this.productType.includes('オーダーカーテン'))
        return 'unavailable'
      else return 'backstock'
    return 'available'
  }

  get priceText() {
    return this.productType.includes('オーダーカーテン')
      ? `${numberWithCommas(this.priceMin)}~`
      : numberWithCommas(this.price)
  }

  get comparePriceText() {
    return this.productType.includes('オーダーカーテン')
      ? `${numberWithCommas(this.comparePriceMin)}~`
      : numberWithCommas(this.comparePrice)
  }

  get plainTitle() {
    const tempDivElement = document.createElement('div')
    tempDivElement.innerHTML = this.title
    return tempDivElement.textContent || tempDivElement.innerText
  }

  get enhancedTitle() {
    return `${this.text1}${this.text2}${this.title}`
  }

  get zetaItemUnit() {
    return this.itemGroupId ? 'item_group_id' : 'code'
  }

  get zetaItemId() {
    return this.itemGroupId || this.handle.toString()
  }

  get isExhibit() {
    return this.productType.includes('展示品')
  }

  /**
   * GIDの末尾の数値を取得する
   * 例: 'gid://shopify/Product/123456789' => 123456789
   * @param gid GID文字列
   * @returns 末尾の数値、または無効な場合はundefined
   */
  static getNumericIdFromGid(gid = '') {
    const matched = gid.toString().match(/\/(\d+)$/)
    return matched ? Number.parseInt(matched[1], 10) : undefined
  }

  /**
   * 価格を整数のセント表記に変換する
   * 例: '12.34' => 1234
   * @param price 価格文字列
   * @returns 整数のセント表記、または無効な場合はundefined
   */
  static toProductPriceUnit(price) {
    if (price === undefined || price === null || price === '') return undefined
    return Math.round(Number.parseFloat(price) * 100)
  }

  static normalizeFavoriteVariant(variant = {}) {
    const id =
      Product.getNumericIdFromGid(variant.variantId) || variant.variantId || ''

    return {
      available: variant.available,
      compare_at_price: Product.toProductPriceUnit(variant.compareAtPrice),
      id,
      inventory_quantity: variant.inventoryQuantity,
      price: Product.toProductPriceUnit(variant.price),
      sku: variant.sku,
    }
  }

  /**
   * 商品とバリアントの情報を正規化して、Productモデルのコンストラクタに渡せる形式に変換する
   * @param product 商品情報オブジェクト
   * @param variant バリアント情報オブジェクト
   * @returns 正規化された商品情報オブジェクト
   */
  static normalizeFavoriteProduct(product, variant) {
    const id =
      Product.getNumericIdFromGid(product.productId) || product.productId
    const normalizedVariant = variant
      ? Product.normalizeFavoriteVariant(variant)
      : undefined

    return {
      available: normalizedVariant?.available,
      compare_at_price: normalizedVariant?.compare_at_price,
      compare_at_price_min: Product.toProductPriceUnit(
        product.compareAtPriceMin
      ),
      featured_image: product.productImage,
      handle: product.handle,
      id,
      inventory_quantity: normalizedVariant?.inventory_quantity,
      price: normalizedVariant?.price,
      price_min: Product.toProductPriceUnit(product.priceMin),
      sku: normalizedVariant?.sku,
      tags: product.tags,
      title: product.title,
      type: product.type,
      url: product.url,
      variants: normalizedVariant?.id ? [{ id: normalizedVariant.id }] : [],
      vendor: product.vendor,
    }
  }

  get isOutlet() {
    return this.productType.includes('アウトレット') // STGは「アウトレット（雑貨）」PRODは「アウトレット品（雑貨）」
  }
}
